import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faSpinner } from '@/fontawesome';
import { APIRoutes } from '@/services/api.routes';
import { showToast } from '@/utils/toasts';
import { useUser } from '@/contexts/UserContext';
import ToggleButton from '@/components/ui/ToggleButton';
import { EMAIL_OPT_OUT_ICONS } from '@/components/admin/common/EmailOptOutIcons';
import { ALL_EMAIL_OPT_OUT_KEYS, EMAIL_OPT_OUT_LABELS, type User, type UserEmailOptOutKey } from '@/models';

interface UserEmailOptOutCardProps {
  user: User;
  onUserUpdated: (updatedUser: User) => void;
}

export default function UserEmailOptOutCard({ user, onUserUpdated }: UserEmailOptOutCardProps) {
  const { isAdmin } = useUser();
  const [savingKey, setSavingKey] = useState<UserEmailOptOutKey | null>(null);

  const handleToggle = async (key: UserEmailOptOutKey) => {
    setSavingKey(key);
    try {
      const updatedUser = await APIRoutes.updateUser(user.id, {
        emailOptOut: { [key]: !user.emailOptOut[key] }
      });
      onUserUpdated(updatedUser);
      showToast('E-Mail-Einstellungen aktualisiert', 'success');
    } catch (error) {
      console.error('Error updating email opt-out:', error);
      showToast('Fehler beim Aktualisieren der E-Mail-Einstellungen', 'error');
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
          <FontAwesomeIcon icon={faEnvelope} className="w-6 h-6 mr-3" />
          E-Mail-Einstellungen
        </h2>
        {!isAdmin && (
          <span className="text-sm text-gray-500 dark:text-gray-400">Nur Administratoren können Änderungen vornehmen</span>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {ALL_EMAIL_OPT_OUT_KEYS.map((key) => {
          const receives = !user.emailOptOut[key];
          return (
            <div key={key} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="flex items-center">
                <FontAwesomeIcon icon={EMAIL_OPT_OUT_ICONS[key]} className="w-4 h-4 mr-3 text-gray-500 dark:text-gray-400" />
                <div>
                  <p className="text-gray-900 dark:text-white font-medium">{EMAIL_OPT_OUT_LABELS[key]}</p>
                  <p className={`text-sm ${receives ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    {receives ? 'Erhält E-Mails' : 'Abgemeldet'}
                  </p>
                </div>
              </div>
              <div className="flex items-center">
                {savingKey === key && (
                  <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 mr-3 text-blue-600 dark:text-blue-400 animate-spin" />
                )}
                <ToggleButton
                  enabled={receives}
                  onClick={() => handleToggle(key)}
                  disabled={!isAdmin || savingKey !== null}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
